import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import useContactInfoCheck from '../../hooks/useContactInfoCheck'
import ApiPlayers from '../../api/ApiPlayers'
import ContactInfoModal from '../leagues/ContactInfoModal'

/**
 * Wrapper component that ensures user has contact info before accessing scheduling features
 * Shows a modal to collect email + phone if missing
 */
export default function RequireContactInfo({ children }) {
  const { user } = useAuth()
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const [primaryPlayer, setPrimaryPlayer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showContactInfoModal, setShowContactInfoModal] = useState(false)
  const contactInfo = useContactInfoCheck(user, primaryPlayer)

  // Load primary player
  useEffect(() => {
    let isMounted = true

    const loadPrimaryPlayer = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      try {
        const players = await ApiPlayers.getMyPlayers()
        const primary = players.find((p) => p.primary_player)
        if (isMounted) {
          setPrimaryPlayer(primary)
          setLoading(false)
        }
      } catch (err) {
        console.error('Failed to load primary player:', err)
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadPrimaryPlayer()

    return () => {
      isMounted = false
    }
  }, [user])

  // Check contact info once loaded
  useEffect(() => {
    if (!loading && !contactInfo.hasContactInfo) {
      setShowContactInfoModal(true)
    }
  }, [loading, contactInfo.hasContactInfo])

  const handleContactInfoComplete = async () => {
    setShowContactInfoModal(false)
    setLoading(true) // Prevent re-check during reload
    // Reload player data
    try {
      const players = await ApiPlayers.getMyPlayers()
      const primary = players.find((p) => p.primary_player)
      if (primary) {
        setPrimaryPlayer(primary)
      }
    } catch (err) {
      console.error('Failed to reload player data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleModalDismiss = () => {
    // User tried to dismiss without completing - redirect back to league
    if (leagueId) {
      navigate(`/leagues/${leagueId}`)
    } else {
      navigate('/my-leagues')
    }
  }

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '200px' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <ContactInfoModal
        show={showContactInfoModal}
        onComplete={handleContactInfoComplete}
        onDismiss={handleModalDismiss}
        primaryPlayer={primaryPlayer}
        user={user}
      />
      {contactInfo.hasContactInfo ? children : null}
    </>
  )
}
